import { eventChannel } from 'redux-saga';
import { all, cancel, cancelled, fork, put, race, take, takeLatest } from 'redux-saga/effects';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

import { db } from 'config/firebase';
import { GET_INCIDENT_REPORTS, LOGOUT_USER, SET_ACTIVE_USER_ORG } from '../actions/types';
import { getIncidentReportsSuccess, getIncidentReportsFailure } from '../actions/IncidentReports';

const incidentReportsCollectionRef = collection(db, 'incident_reports');

// Single live subscription per org — org_id is the only server-side query constraint.
// Every other filter (severity/category/status/review_stage/violation/guard/location/
// property/date) is applied client-side (see filterIncidentReports in reducers/IncidentReports.js)
// against this same full list, so the filter panel never re-subscribes and the
// Location/Property dropdown options can always be built from every report the org has,
// not just whichever ones the current filter selection happens to match.
function* incidentReportsCollectionWatch(orgId) {
    let unsubscribe;

    const channel = eventChannel(emit => {
        const incidentReportsQuery = query(incidentReportsCollectionRef, where('org_id', '==', orgId));
        unsubscribe = onSnapshot(
            incidentReportsQuery,
            snap => emit({ docs: snap.docs.map(docSnap => docSnap.data()) }),
            error => emit({ error })
        );
        return () => unsubscribe();
    });

    try {
        while (true) {
            const { userSignOut, resettingOrg, data } = yield race({
                userSignOut: take(LOGOUT_USER),
                resettingOrg: take(SET_ACTIVE_USER_ORG),
                data: take(channel)
            });

            if (userSignOut || resettingOrg) {
                channel.close();
                return;
            }

            if (data?.error) {
                yield put(
                    getIncidentReportsFailure(data.error?.message || 'Failed to load incident reports')
                );
                continue;
            }

            const sorted = [...data.docs].sort((a, b) => {
                const aMs = a.reported_at?.seconds ?? 0;
                const bMs = b.reported_at?.seconds ?? 0;
                return bMs - aMs;
            });
            yield put(getIncidentReportsSuccess(sorted));
        }
    } finally {
        if (unsubscribe) unsubscribe();
        if (yield cancelled()) {
            channel.close();
        }
    }
}

let liveTask = null;

function* startIncidentReportsLive({ payload }) {
    const { orgId } = payload;
    if (!orgId) return;

    if (liveTask) {
        yield cancel(liveTask);
        liveTask = null;
    }

    liveTask = yield fork(incidentReportsCollectionWatch, orgId);
}

export function* watchGetIncidentReports() {
    yield takeLatest(GET_INCIDENT_REPORTS, startIncidentReportsLive);
}

export default function* rootSaga() {
    yield all([fork(watchGetIncidentReports)]);
}
